import { t } from '../../i18n/lingua'
import { SceltaCIParametro, type ParametroScelto } from './SceltaCIParametro'

/** What a series of an interface shows: its value (utilisation) or one of
 *  the derived series the probe keeps beside it. */
export type MisuraSerie = 'valore' | 'in' | 'out' | 'pkts_in' | 'pkts_out' | 'errors' | 'discards'

/** The derived series of an interface, with the unit each one is drawn in. */
export const MISURE_SERIE: { value: MisuraSerie; label: () => string; unit: string }[] = [
  { value: 'valore', label: () => t('utilizzo (%)'), unit: '%' },
  { value: 'in', label: () => t('traffico in entrata (Mbit/s)'), unit: 'Mbit/s' },
  { value: 'out', label: () => t('traffico in uscita (Mbit/s)'), unit: 'Mbit/s' },
  { value: 'pkts_in', label: () => t('pacchetti in entrata (pkt/s)'), unit: 'pkt/s' },
  { value: 'pkts_out', label: () => t('pacchetti in uscita (pkt/s)'), unit: 'pkt/s' },
  { value: 'errors', label: () => t('errori nell’intervallo'), unit: '' },
  { value: 'discards', label: () => t('scarti nell’intervallo'), unit: '' },
]

/** The unit a series is drawn in: the derived series have their own, the value keeps the parameter's. */
export function unitaDiMisura(misura: MisuraSerie | undefined, unit: string | undefined): string {
  if (!misura || misura === 'valore') return unit ?? ''
  return MISURE_SERIE.find((m) => m.value === misura)?.unit ?? ''
}

/** A short word for the legend: «entrata», «uscita», «pacchetti in»… */
function suffissoMisura(misura: MisuraSerie): string {
  switch (misura) {
    case 'in': return t('entrata')
    case 'out': return t('uscita')
    case 'pkts_in': return t('pacchetti in entrata')
    case 'pkts_out': return t('pacchetti in uscita')
    case 'errors': return t('errori')
    case 'discards': return t('scarti')
    default: return ''
  }
}

/** The label with the measure's word at the end, replacing a previous one. */
function conSuffisso(label: string, misura: MisuraSerie): string {
  let base = label
  for (const m of MISURE_SERIE) {
    const s = suffissoMisura(m.value)
    if (s && base.endsWith(' · ' + s)) base = base.slice(0, -(' · ' + s).length)
  }
  const s = suffissoMisura(misura)
  return s ? `${base} · ${s}` : base
}

/** A series of the custom chart, as saved in the widget's options. */
export interface SerieScelta {
  ci_id: string
  key: string
  ci_name: string
  /** The name in the legend: proposed from CI, parameter and port description, editable. */
  label: string
  unit?: string
  kind?: string
  /** For an interface: which of its series to draw (default: the value, utilisation). */
  misura?: MisuraSerie
}

export const MAX_SERIE = 8

/** The label proposed for a series: CI, parameter and — for an interface — the port description. */
export function etichettaProposta(ciName: string, p: ParametroScelto): string {
  const descrizione = p.detail.split(' · ')[0]
  return [ciName, p.name, p.kind === 'interface' && descrizione && !/^\d+\.\d+\.\d+\.\d+$/.test(descrizione) && !['up', 'down'].includes(descrizione) ? descrizione : '']
    .filter(Boolean).join(' · ')
}

/**
 * The series of a custom chart: one row each — CI and parameter (with the
 * port description in the list), the label for the legend — plus «Aggiungi
 * serie». Up to eight: more lines than that are not read, they are counted.
 */
export function SceltaSerie({ serie, onCambia, soloTipi, max = MAX_SERIE }: {
  serie: SerieScelta[]
  onCambia: (serie: SerieScelta[]) => void
  soloTipi?: string[]
  /** Quante al massimo: otto in un grafico (oltre non si leggono), sedici in una parete di schede. */
  max?: number
}) {
  const cambia = (i: number, parte: Partial<SerieScelta>) => onCambia(serie.map((s, j) => (j === i ? { ...s, ...parte } : s)))
  return (
    <div className="serie-scelta">
      {serie.map((s, i) => (
        <fieldset key={i} className="serie-scelta__riga">
          <legend>{t('Serie {n}', { n: i + 1 })}</legend>
          <div className="form-grid">
            <SceltaCIParametro ciId={s.ci_id} chiave={s.key} soloTipi={soloTipi}
                               onCambia={(ci_id, key, ci_name, p) => cambia(i, {
                                 ci_id, key, ci_name, unit: p?.unit, kind: p?.kind,
                                 label: p ? etichettaProposta(ci_name, p) : s.label,
                               })} />
            {s.kind === 'interface' && (
              <label className="field span-2">
                <span className="field__label">{t('Misura')}</span>
                <select value={s.misura ?? 'valore'} aria-label={t('Misura della serie {n}', { n: i + 1 })}
                        onChange={(e) => {
                          const misura = e.target.value as MisuraSerie
                          cambia(i, { misura, label: conSuffisso(s.label, misura) })
                        }}>
                  {MISURE_SERIE.map((m) => <option key={m.value} value={m.value}>{m.label()}</option>)}
                </select>
              </label>
            )}
            <label className={`field ${s.kind === 'interface' ? 'span-2' : 'span-4'}`}>
              <span className="field__label">{t('Etichetta nella legenda')}</span>
              <input type="text" value={s.label} onChange={(e) => cambia(i, { label: e.target.value })} maxLength={80} />
            </label>
            <div className="field span-2 serie-scelta__azioni">
              <button type="button" className="linklike" onClick={() => onCambia(serie.filter((_, j) => j !== i))}>{t('togli la serie')}</button>
            </div>
          </div>
        </fieldset>
      ))}
      {serie.length < max && (
        <button type="button" className="secondary" onClick={() => onCambia([...serie, { ci_id: '', key: '', ci_name: '', label: '' }])}>
          {t('Aggiungi serie')}
        </button>
      )}
      {serie.length >= max && <p className="muted eagle-piccolo">{t('Al massimo {n} serie qui: per averne altre aggiungi un secondo widget.', { n: max })}</p>}
    </div>
  )
}
